import type React from 'react';
import { useEffect, useState } from 'react';
import type { StepProps } from '../../types/onboarding.types';
import { SUGGESTIONS_MAP } from '../../utils/constants';
import Suggestions from '../shared/Suggestions';

const ServicesStep: React.FC<StepProps> = ({
  data,
  onChange,
  validation,
  onNext,
  onBack,
  isLoading,
  error,
  selectedRole,
}) => {
  const [input, setInput] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    setLocalError(null);
  }, []);

  const handleAddService = (service: string) => {
    const trimmed = service.trim();
    if (!trimmed) {
      setLocalError('Please enter a service');
      return;
    }

    if (data.services.includes(trimmed)) {
      setLocalError('This service has already been added');
      return;
    }

    onChange({ ...data, services: [...data.services, trimmed] });
    setInput('');
    setLocalError(null);
  };

  const handleRemoveService = (service: string) => {
    const updated = data.services.filter((s) => s !== service);
    onChange({ ...data, services: updated });
    setLocalError(null);
  };

  const handleInputChange = (value: string) => {
    setInput(value);
    setLocalError(null);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleAddService(suggestion);
    setLocalError(null);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddService(input);
    }
  };

  const handleSubmit = async () => {
    if (data.services.length === 0) {
      setLocalError('Please add at least one service');
      return;
    }

    const isValid = await validation(data);
    if (isValid) {
      setLocalError(null);
      onNext();
    } else {
      setLocalError('Please add at least one service');
    }
  };

  return (
    <div className="space-y-6">
      {/* Suggestions */}

      <div className="mb-[28px]">
        <p className="flex items-center text-heading-4 font-paragraph mb-2 text-[#737982]">
          Suggestions
        </p>
        <Suggestions
          suggestions={SUGGESTIONS_MAP.Services}
          onSuggestionClick={handleSuggestionClick}
          layout="default"
        />
      </div>

      {/* Input field for direct entry */}
      <div className="relative w-full max-w-[508px]">
        <input
          type="text"
          value={input}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Add services"
          className="w-full max-w-[508px] text-body-text-large font-paragraph border border-border-gray rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200 px-4 py-2 text-[#737982]"
        />
      </div>

      {/* Added Services Display */}
      {data.services.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-medium text-gray-700 mb-2">
            Added Services ({data.services.length})
          </h3>
          <div className="flex flex-wrap gap-2 max-h-[250px] overflow-y-auto font-paragraph text-heading-4">
            {data.services.map((service, index) => (
              <div key={`${service}-${index}`} className="flex items-center m-1">
                <div className="bg-primary text-white rounded-l-[4px] px-3 py-1 flex items-center">
                  {service}
                </div>
                <button
                  onClick={() => handleRemoveService(service)}
                  className="bg-primary text-white rounded-r-md ml-[2px] px-[4px] py-1 flex items-center hover:bg-red-700 transition-colors"
                >
                  <XIcon className="w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {(error || localError) && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error || localError}
        </div>
      )}

      {/* Hidden navigation for parent component to handle */}
      <div className="hidden">
        <button onClick={handleSubmit} disabled={isLoading || data.services.length === 0} />
      </div>
    </div>
  );
};

// Import XIcon from the UI library
import { XIcon } from '@commander/ui';

export default ServicesStep;
